import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, mergeProps as _mergeProps, vShow as _vShow, withDirectives as _withDirectives, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = { class: "tooltip-body" }
const _hoisted_2 = {
  key: 0,
  class: "text-center text-xl-bold"
}
const _hoisted_3 = {
  key: 1,
  class: "text-center text-xl racq-med-grey mb-4"
}
const _hoisted_4 = { class: "text-center" }
const _hoisted_5 = ["innerHTML"]
const _hoisted_6 = { class: "my-6" }
const _hoisted_7 = { class: "tooltip-footer" }
const _hoisted_8 = { class: "w-full" }
const _hoisted_9 = ["onclick"]
const _hoisted_10 = { class: "w-full" }
const _hoisted_11 = ["onclick"]

import { ref, onMounted, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { cloneDeep } from 'lodash-es';

// 2. Third-Party Libraries
import { Field, RichText } from '@sitecore-jss/sitecore-jss-vue';

// 3. Interfaces & Types
import { IFormStep, IQuote, SubmitQuote, QuoteTypeCodes, IPaymentSectionProps } from '@/interfaces';
import { InformationBoxTypes } from '@/interfaces/forms/information-box.interface';

// 4. Stores
import {
  useFormStore,
  useReferenceDataStore,
  usePaymentStore,
  useConfigSettingsStore,
  IQConfigSettings,
  SettingCategory,
} from '@/store';

// 5. Enums & Constants
import { PaymentTypes } from '@/enums/payment-types.enum';
import { PaymentFrequency } from '@/enums/payment-frequency';
import { JP_GENERIC_CODE } from '@/constants/jeopardy-codes.const';

// 6. Utilities & Composables
import { toBool, toCurrency } from '@/utils/convert';
import useFormInputData from '@/composables/useFormInputData';

// 7. Components
import DollarAmount from '@/components/DollarAmount/DollarAmount.vue';
import ExpandableContent from '@/components/ExpandableContent/ExpandableContent.vue';
import InformationBox from '@/components/InformationBox/InformationBox.vue';
import IQPaymentError from '@/components/IQPaymentError/IQPaymentError.vue';
import ModalDialog from '@/components/ModalDialog/ModalDialog.vue';
import PaymentMethods from '@/components/PaymentMethods/PaymentMethods.vue';
import PaymentSection from '@/components/PaymentSection/PaymentSection.vue';
import RACQFormKit from '@/components/RACQFormKit/RACQFormKit.vue';
import SectionPanel from '@/components/SectionPanel/SectionPanel.vue';
import PaymentHeader from '@/components/PaymentHeader/PaymentHeader.vue';

// Define props and emitters
// ------------------------------------------------------------- //

export const emitSubmitQuote = 'emitSubmitQuote';
export const emitTermsAndConditionsChanged = 'emitTermsAndConditionsChanged';

export default /*@__PURE__*/_defineComponent({
  __name: 'IQPaymentPanel',
  props: {
    step: {},
    quote: {},
    paymentTitle: {},
    paymentSubtitle: {},
    fields: {},
    errors: {}
  },
  emits: [emitSubmitQuote, emitTermsAndConditionsChanged],
  setup(__props: any, { emit: __emit }) {

// 1. Vue Core Imports
const props = __props;

// Define Store
// ------------------------------------------------------------- //
const formStore = useFormStore();
const paymentStore = usePaymentStore();
const refStore = useReferenceDataStore();
const configSettingsConfigStore = useConfigSettingsStore();

const translation = useI18n();
const dictionary: Record<string, string> = translation.getLocaleMessage('en') as any;

const emit = __emit;
const paymentFrequencyPriceSuffix = ref<string>('');

// Define Composables
// ------------------------------------------------------------- //
const { formInputData } = useFormInputData(props.step.formInputs!);

// Define Component state
// ------------------------------------------------------------- //
const formData = ref<Record<string, any>>({});
const insuranceType = ref<string>();
const showPayLaterModal = ref<boolean>(false);
const isAnnual = computed(() => paymentStore.selectedPaymentFrequency == PaymentFrequency.Annually);

const premium = computed(() => {
  if (isAnnual.value) {
    return props.quote?.insuranceQuoteSummary?.yearlyAnnualPremium;
  } else {
    return props.quote?.insuranceQuoteSummary?.monthlyDeposit;
  }
});

const paylaterPremium = computed(() => {
  return props.quote?.insuranceQuoteSummary?.yearlyCallCenterPremium;
});

const paymentMethodFields = computed(() => {
  let fields = cloneDeep(props.step.formInputs?.paymentMethod?.fields[0]) as IPaymentSectionProps;

  fields.AcceptPaymentTypes = filteredAcceptanceTypes();

  return fields;
});

const showOnlineDiscountWarning = computed(() => {
  return (
    props.quote.discounts?.totalYearlyOnlineDiscountAmount?.value ||
    props.quote.discounts?.totalMonthlyOnlineDiscountAmount?.value
  );
});

function filteredAcceptanceTypes() {
  let filtered = props.step.formInputs?.paymentMethod?.fields[0].AcceptPaymentTypes.filter(
    (x: any) => x.name !== 'PayLater' && x.name !== 'PayPal'
  );

  filtered = filtered.filter(
    (type: any) =>
      (paymentStore.selectedPaymentFrequency === PaymentFrequency.Annually &&
        toBool(formData.value.futureRenewal) &&
        type.name !== PaymentTypes[PaymentTypes.CreditCard]) ||
      paymentStore.selectedPaymentFrequency !== PaymentFrequency.Annually ||
      !toBool(formData.value.futureRenewal)
  );

  return filtered;
}
// Lifecycle hooks
// ------------------------------------------------------------- //
onMounted(async () => {
  paymentStore.applyTermsAndConditionsValidationFix = true;

  const submissionRetries = (await configSettingsConfigStore.getSetting(
    IQConfigSettings.PaymentSubmissionRetries,
    SettingCategory.IQSettings
  )) as number;
  paymentStore.maxRetries = submissionRetries ?? 3;
  await initialisePayment();

  formData.value.futureRenewal = (paymentStore.selectedPaymentFrequency == PaymentFrequency.Monthly).toString();
});

watch(
  () => formData.value.paymentTermsAndConditions,
  (newValue) => {
    paymentStore.disablePaymentPanel = true;
    paymentStore.termsAndConditionsAccepted = newValue;
    emit(emitTermsAndConditionsChanged);
  }
);

watch(
  () => paymentStore.selectedPaymentFrequency,
  (newVal) => {
    formData.value.futureRenewal = (newVal == PaymentFrequency.Monthly).toString();
    setPaymentPriceSuffix();
    unTickTermsAndConditions();
  }
);

watch(
  () => formData.value.futureRenewal,
  (newValue) => {
    setAutoRenewPostContent();
    unTickTermsAndConditions();
  }
);

watch(
  () => paymentStore.paymentRetryAttempts,
  async function () {
    //This is done to reset the zebra iframe so payment can be attempted again.
    await resetPaymentIframe();
    await initialisePayment();

    if (paymentStore.hasExceededMaxPaymentAttempts()) {
      formStore.setDisableAllSteps();
    }
  }
);

// Define Functions
function unTickTermsAndConditions() {
  paymentStore.termsAndConditionsAccepted = false;
  formData.value.paymentTermsAndConditions = false;

  //Reset iframe otherwise it can be blank sometimes.
  paymentStore.resetIframe();
  paymentStore.selectedPaymentMethod = paymentStore.getDefaultSelectedPaymentMethod(filteredAcceptanceTypes());

  emit(emitTermsAndConditionsChanged);
}
// ------------------------------------------------------------- //

async function initialisePayment() {
  /** Set dynamic contents */
  paymentStore.submitPaymentCallback = () => onSubmit(false);
  paymentStore.disablePaymentPanel = !formData.value.paymentTermsAndConditions;
  paymentStore.selectedRenewalPreference = props.quote.renewalPreference as boolean;

  setPaymentPriceSuffix();
  setDiscountLabel();
  setAutoRenewPostContent();
  insuranceType.value = await refStore.getInsuranceProductName(QuoteTypeCodes[props.quote.quoteType!]);

  /** Map payment details for FZ Iframe */
  paymentStore.amount = premium?.value?.toString() ?? '0';
  paymentStore.buttonText = props.step.formInputs?.paymentMethod?.fields[0]?.SubmitPaymentButtonLabel?.value;
  paymentStore.reference = props.quote?.reference ?? '';
}

function setDiscountLabel() {
  const paymentSection = props.step.formInputs?.paymentMethod?.fields[0];

  if (!paymentSection) return; // Safeguard against undefined `paymentSection`

  if (isAnnual.value) {
    paymentSection.PayLaterContent.value = paymentSection.PayLaterContent?.value.replace(
      '{{onlineDiscount}}',
      dictionary.IQ_OnlineDiscount.replace(
        '$#value#',
        toCurrency(props.quote.discounts?.totalYearlyOnlineDiscountAmount?.value)
      )
    );
  } else {
    paymentSection.PayLaterContent.value = paymentSection.PayLaterContent?.value.replace(
      '{{onlineDiscount}}',
      dictionary.IQ_OnlineDiscount.replace(
        '$#value#',
        toCurrency(props.quote.discounts?.totalMonthlyOnlineDiscountAmount?.value)
      )
    );
  }
}

function setPaymentPriceSuffix() {
  const optionsValue = props.step.formInputs?.paymentFrequencyPricePrefix?.options.value;
  if (!optionsValue) return;

  const pricePrefixes = Object.fromEntries(new URLSearchParams(optionsValue));
  const enumKey = PaymentFrequency[formData.value.paymentFrequency];

  paymentFrequencyPriceSuffix.value = pricePrefixes[enumKey] || '';
}

async function setAutoRenewPostContent() {
  if (!props.fields.isAutoRenewalPreferenceEnabled) return;

  paymentStore.selectedRenewalPreference = toBool(formData.value.futureRenewal);

  const value =
    formData.value.futureRenewal == 'true'
      ? props.step.formInputs?.futureRenewalOn?.label?.value
      : props.step.formInputs?.futureRenewalOff?.label?.value;

  // Show post content
  formStore.updateInput({
    inputId: 'futureRenewal',
    updates: {
      showPostContent: true,
      postContent: {
        value,
      },
    },
  });
}

async function resetPaymentIframe() {
  paymentStore.reference = '';
}

async function onSubmit(isPayLater?: boolean) {
  try {
    if (paymentStore.paymentRetryAttempts >= paymentStore.maxRetries) return;

    if (isPayLater) {
      paymentStore.selectedPaymentMethod = PaymentTypes.PayLater;
    }

    formStore.disableAllSteps = true;

    let submitQuote = null;

    if (isPayLater) {
      //Force to Annual for pay later and trigger update to selected payment frequency.
      paymentStore.selectedPaymentFrequency = PaymentFrequency.Annually;
      paymentStore.selectedPaymentFrequencyDisplayText = paymentStore.paymentFrequencyWording?.annual;

      submitQuote = new SubmitQuote().createSubmitQuoteUsingPayLater(PaymentFrequency.Annually);
      showPayLaterModal.value = false;
    } else {
      switch (paymentStore.selectedPaymentMethod) {
        case PaymentTypes.CreditCard:
          submitQuote = new SubmitQuote().createSubmitQuoteUsingCardPaymentDetails(paymentStore.cardDetails);
          break;
        case PaymentTypes.BankAccount:
          if (paymentStore.directDebitDetails) {
            submitQuote = new SubmitQuote().createSubmitQuoteUsingBankAccountDetails(paymentStore.directDebitDetails);
          }
          break;
      }
    }

    if (!submitQuote) {
      throw JP_GENERIC_CODE;
    }

    emit(emitSubmitQuote, submitQuote);
  } catch (jPCode: any) {
    formStore.showLoadingForCurrentStep(false);
  }
}

function onShowModal() {
  showPayLaterModal.value = true;
}

function onModalClose(e: any) {
  showPayLaterModal.value = e;
}

return (_ctx: any,_cache: any) => {
  const _component_FormKit = _resolveComponent("FormKit")!

  return (_openBlock(), _createBlock(_component_FormKit, {
    modelValue: formData.value,
    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formData).value = $event)),
    type: "form",
    disabled: !_ctx.step.isActive,
    onSubmit: () => {}
  }, {
    default: _withCtx(() => [
      _createVNode(PaymentHeader, {
        titles: {
        primary: _ctx.paymentTitle,
        secondary: _ctx.paymentSubtitle,
      },
        premiumInfo: {
        monthly: props.quote?.insuranceQuoteSummary?.monthlyDeposit!,
        annual: props.quote?.insuranceQuoteSummary?.yearlyAnnualPremium!,
        frequencyLabel: paymentFrequencyPriceSuffix.value,
        adminFee: props.quote?.insuranceQuoteSummary
          ? props.quote?.insuranceQuoteSummary?.payByTheMonthFee / 12
          : undefined,
        discount: {
          monthly: props.quote.discounts?.totalMonthlyOnlineDiscountAmount?.value,
          annual: props.quote.discounts?.totalYearlyOnlineDiscountAmount?.value,
        },
      },
        stepFormInputs: _ctx.step.formInputs!,
        showPaymentFrequencyDropdown: true
      }, null, 8, ["titles", "premiumInfo", "stepFormInputs"]),
      _createVNode(ExpandableContent, {
        class: "input-spacing",
        toggleButtonExpandText: _ctx.step.formInputs?.needToPayLater?.toggleButtonExpandText?.value,
        toggleButtonCollapseText: _ctx.step.formInputs?.needToPayLater?.toggleButtonCollapseText?.value,
        dynamicExpander: false
      }, {
        introContent: _withCtx(() => [
          _createVNode(_unref(RichText), {
            field: { value: _ctx.step.formInputs?.needToPayLater?.introContent?.value }
          }, null, 8, ["field"])
        ]),
        conditionalContent: _withCtx(() => [
          _createVNode(_unref(RichText), {
            field: { value: _ctx.step.formInputs?.needToPayLater?.conditionalContent?.value },
            class: "pt-3"
          }, null, 8, ["field"]),
          _createElementVNode("button", {
            onclick: onShowModal,
            type: "button",
            class: "btn btn-primary w-full md:w-max mt-5 !py-1"
          }, _toDisplayString(_ctx.step.formInputs?.payLaterModal?.modalTriggerLinkText?.value), 1)
        ]),
        _: 1
      }, 8, ["toggleButtonExpandText", "toggleButtonCollapseText"]),
      (_ctx.fields.isAutoRenewalPreferenceEnabled)
        ? (_openBlock(), _createBlock(SectionPanel, {
            key: 0,
            title: { value: _unref(formInputData)('futureRenewalTitle')?.label },
            class: "mb-6"
          }, {
            sectionPanelContent: _withCtx(() => [
              _createVNode(RACQFormKit, {
                bindings: _unref(formInputData)('futureRenewal')
              }, null, 8, ["bindings"]),
              (_unref(paymentStore).selectedPaymentFrequency === _unref(PaymentFrequency).Annually && _unref(toBool)(formData.value?.futureRenewal))
                ? (_openBlock(), _createBlock(InformationBox, {
                    key: 0,
                    type: _unref(InformationBoxTypes).INFO,
                    title: _ctx.step.formInputs?.payingWithACardInfoBox?.title?.value,
                    description: _ctx.step.formInputs?.payingWithACardInfoBox?.content?.value,
                    class: "mt-6"
                  }, null, 8, ["type", "title", "description"]))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }, 8, ["title"]))
        : _createCommentVNode("", true),
      _createVNode(SectionPanel, {
        title: { value: _unref(formInputData)('paymentTermsAndConditionsTitle')?.label },
        class: "mb-6"
      }, {
        sectionPanelContent: _withCtx(() => [
          _createVNode(RACQFormKit, {
            bindings: { ..._unref(formInputData)('paymentTermsAndConditions'), validationVisibility: 'submit' }
          }, null, 8, ["bindings"])
        ]),
        _: 1
      }, 8, ["title"]),
      _createVNode(IQPaymentError, {
        step: _ctx.step,
        errors: _ctx.errors
      }, null, 8, ["step", "errors"]),
      _withDirectives(_createVNode(SectionPanel, {
        title: { value: _ctx.step.formInputs?.paymentMethod?.SectionTitle?.value }
      }, {
        sectionPanelContent: _withCtx(() => [
          (_openBlock(), _createBlock(PaymentSection, _mergeProps({
            key: `${_unref(paymentStore).selectedPaymentFrequency}-${formData.value.futureRenewal}-${_unref(paymentStore).selectedPaymentMethod}`
          }, paymentMethodFields.value), null, 16))
        ]),
        _: 1
      }, 8, ["title"]), [
        [_vShow, 
        !_unref(paymentStore).submissionSuccessful &&
        !_unref(paymentStore).disablePaymentPanel &&
        _unref(paymentStore).termsAndConditionsAccepted
      ]
      ]),
      _createVNode(ModalDialog, {
        modelValue: showPayLaterModal.value,
        showXCloseButton: true,
        closeOnEsc: true,
        size: "md",
        "onUpdate:modelValue": _cache[0] || (_cache[0] = (value) => onModalClose(value))
      }, {
        default: _withCtx(() => [
          _cache[3] || (_cache[3] = _createElementVNode("div", { class: "sticky-modal-header" }, [
            _createElementVNode("div", { class: "text-xl-bold" }, "Pay later")
          ], -1)),
          _createElementVNode("div", _hoisted_1, [
            (_ctx.paymentTitle)
              ? (_openBlock(), _createElementBlock("div", _hoisted_2, _toDisplayString(_ctx.paymentTitle), 1))
              : _createCommentVNode("", true),
            (_ctx.paymentSubtitle)
              ? (_openBlock(), _createElementBlock("div", _hoisted_3, _toDisplayString(_ctx.paymentSubtitle), 1))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_4, [
              _createVNode(DollarAmount, {
                amount: paylaterPremium.value,
                showStarSign: false
              }, {
                default: _withCtx(() => _cache[2] || (_cache[2] = [
                  _createElementVNode("span", { class: "text-m ml-1" }, "per year", -1)
                ])),
                _: 1
              }, 8, ["amount"]),
              _createVNode(_unref(RichText), {
                class: "text-m racq-dark-grey pt-2",
                field: _ctx.step.formInputs?.payLaterPriceNoOnlineDiscount?.label
              }, null, 8, ["field"])
            ]),
            (showOnlineDiscountWarning.value)
              ? (_openBlock(), _createBlock(InformationBox, {
                  key: 2,
                  type: _unref(InformationBoxTypes).WARNING,
                  description: _ctx.step.formInputs?.payLaterInfoBox?.content?.value,
                  class: "my-6"
                }, null, 8, ["type", "description"]))
              : _createCommentVNode("", true),
            _createElementVNode("div", {
              class: _normalizeClass(["richtext", !showOnlineDiscountWarning.value && 'mt-6']),
              innerHTML: _ctx.step.formInputs?.payLaterModal?.modalBody?.value
            }, null, 10, _hoisted_5),
            _createElementVNode("div", _hoisted_6, [
              _createVNode(PaymentMethods, {
                paymentMethods: _ctx.step.formInputs?.paymentMethods?.paymentMethods,
                showAdditionalContent: false,
                filterPaymentMethods: false
              }, null, 8, ["paymentMethods"])
            ]),
            _createVNode(InformationBox, {
              type: _unref(InformationBoxTypes).WARNING,
              description: _ctx.step.formInputs?.payLaterPaymentDueInfoBox?.content?.value,
              class: "my-6"
            }, null, 8, ["type", "description"]),
            (_ctx.step.formInputs?.payLaterDisclaimer?.label?.value && showOnlineDiscountWarning.value)
              ? (_openBlock(), _createBlock(_unref(RichText), {
                  key: 3,
                  field: _ctx.step.formInputs?.payLaterDisclaimer?.label,
                  class: "mt-6 mb-2"
                }, null, 8, ["field"]))
              : _createCommentVNode("", true)
          ]),
          _createElementVNode("div", _hoisted_7, [
            _createElementVNode("div", _hoisted_8, [
              _createElementVNode("button", {
                onclick: () => onSubmit(true),
                type: "button",
                class: "btn btn-md btn-primary w-full"
              }, _toDisplayString(_ctx.step.formInputs?.payLaterModal?.callToActionButtonText?.value), 9, _hoisted_9)
            ]),
            _createElementVNode("div", _hoisted_10, [
              _createElementVNode("button", {
                onclick: () => onModalClose(false),
                type: "button",
                class: "btn btn-md btn-secondary w-full mt-5"
              }, _toDisplayString(_ctx.step.formInputs?.payLaterModal?.closeButtonText?.value), 9, _hoisted_11)
            ])
          ])
        ]),
        _: 1
      }, 8, ["modelValue"])
    ]),
    _: 1
  }, 8, ["modelValue", "disabled"]))
}
}

})